import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isPermissionRefused, type CarrierOption } from '@citibox-panel/api-client';

export interface UseLocationCarriers {
  options: CarrierOption[];
  isPending: boolean;
  isError: boolean;
}

// The flags carry the state, not the list: `options` is empty while pending, on a failed read and on
// a building nobody has delivered at — three cases the caller has to tell apart to say so.
export function useLocationCarriers(
  locationId: number,
  fetchLocationCarriers: (locationId: number) => Promise<CarrierOption[]>,
): UseLocationCarriers {
  // Keyed by building and deliberately independent of ['location-deliveries']: a date-range change
  // or a delivery's state transition does not change which carriers have delivered here, so this
  // read is neither refetched by the filters nor invalidated by a write.
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['location-carriers', locationId],
    queryFn: () => fetchLocationCarriers(locationId),
  });

  // Memoized because the consumer lists `options` as an effect dependency: a bare `data ?? []` is a
  // fresh array on every render while the read has no data, which turns that effect into a loop.
  const options = useMemo(() => data ?? [], [data]);

  // A permission refusal is the host's to show: it replaces the whole view, so reporting it here
  // as a failed read would only paint an inline failure under the access screen.
  return { options, isPending, isError: isError && !isPermissionRefused(error) };
}
